import { useEffect } from 'react'
import type { ActivityWithSessions } from '../../../types/GeneralTypes'
import { msUntilSoonThreshold, msUntilLive, msUntilEnd } from '../helpers/activitySyncHelpers'

/**
 * Schedules precise setTimeout calls at each activity's SOON, LIVE, and END thresholds.
 * Calls `onTick` at each threshold so the caller can re-evaluate time-sensitive state.
 * All timers are cleared when activities change or the component unmounts.
 *
 * Use alongside useActivityStatusTick for belt-and-suspenders coverage:
 * - Scheduler: fires exactly at each threshold (precision)
 * - Tick: fires every 30s as a safety fallback (reliability)
 */
export const useActivityThresholdScheduler = (activities: ActivityWithSessions[], onTick: () => void): void => {
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = []

    for (const activity of activities) {
      const schedule = (ms: number | null) => {
        if (ms !== null && ms > 0) {
          timers.push(setTimeout(onTick, ms))
        }
      }

      schedule(msUntilSoonThreshold(activity.startDate))
      schedule(msUntilLive(activity.startDate))
      schedule(msUntilEnd(activity.endDate))
    }

    return () => timers.forEach(clearTimeout)
    // onTick intentionally excluded — it should be a stable reference (useCallback at call site)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activities])
}
